'use client';

import { EllipsisHorizontalIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { Button, HeroIcon, Typography } from '@/components/atoms';
import {
  LikeButton,
  PlayAllButton,
  PlaylistImage,
} from '@/components/molecules';
import { FavouriteType } from '@/lib/contexts/favouriting';
import { useMediaController } from '@/lib/hooks/media-controller/useMediaController';
import { cn } from '@/lib/utils/tailwind';

import { ContentTypeVariations } from '../CardCarousel';
import { PlaylistOptionsMenu } from '../TrackList/TrackListComponents/PlaylistOptionsMenu';

import type {
  Album,
  NewsItem,
  Playlist,
  Podcast,
  PersonalPlaylist,
  FavoritesForController,
  RecentsForController,
} from '@/lib/api';

type AudioHeaderProps = {
  title: string;
  media: string;
  contentType: ContentTypeVariations;
  className?: string;
  rawdata?:
    | Album
    | Playlist
    | NewsItem
    | Podcast
    | PersonalPlaylist
    | FavoritesForController
    | RecentsForController
    | {
        id: string;
        chapters: {
          podcast_id: number;
          id: number;
          image_url?: string;
          title: string;
          description?: string;
        }[];
      }
    | undefined;
  isLikable?: boolean;
  libraryImages?: { src: string; title: string }[];
};

export const AudioHeader = ({
  title,
  media,
  contentType,
  className,
  rawdata,
  isLikable = true,
  libraryImages,
}: AudioHeaderProps) => {
  const t = useTranslations();
  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false);
  const [playlistTitle, setPlaylistTitle] = useState(title);
  const isPersonalPlaylist =
    contentType === ContentTypeVariations.PERSONAL_PLAYLIST;
  const isFavoritePlaylist =
    contentType === ContentTypeVariations.FAVORITE_PLAYLIST;
  const isRecentPlaylist =
    contentType === ContentTypeVariations.RECENT_PLAYLIST;

  const { isPlaying, currentCompilationId, currentCompilationType } =
    useMediaController();

  const contentTypeTranslated = isPersonalPlaylist
    ? t('B2B_PERSONAL_PLAYLIST_PAGE_CONTENT_TYPE')
    : isFavoritePlaylist
      ? t('B2B_LIBRARY_FAV_TRACKS')
      : isRecentPlaylist
        ? t('B2B_recent_tracks_title')
        : t(`CONTENT_TYPE_${contentType.toUpperCase()}`);

  const playAllData =
    isPersonalPlaylist && typeof rawdata !== 'undefined'
      ? {
          ...(rawdata as PersonalPlaylist),
          id: (rawdata as PersonalPlaylist).playListId,
        }
      : rawdata;

  return (
    <>
      <header
        className={cn('flex flex-col mt-(--vertical-spacing-4)', className)}
      >
        <div
          className={cn(
            'flex flex-col md:flex-row items-start md:items-end gap-4',
          )}
        >
          {isPersonalPlaylist && typeof rawdata !== 'undefined' ? (
            <div className="w-full flex justify-center md:w-auto md:flex-none">
              <PlaylistImage
                images={(rawdata as PersonalPlaylist).playLists.map(track => ({
                  src: track.track_metadata?.album?.pictureUrl ?? '',
                  title: track.track_metadata?.album?.title ?? '',
                }))}
                size={260}
              />
            </div>
          ) : isFavoritePlaylist && typeof rawdata !== 'undefined' ? (
            <div className="w-full flex justify-center md:w-auto md:flex-none">
              <PlaylistImage
                images={(rawdata as FavoritesForController).tracks.map(
                  track => ({
                    src: track.track_metadata?.album?.pictureUrl ?? '',
                    title: track.track_metadata?.album?.title ?? '',
                  }),
                )}
                size={260}
              />
            </div>
          ) : isRecentPlaylist && typeof rawdata !== 'undefined' ? (
            <div className="w-full flex justify-center md:w-auto md:flex-none">
              <PlaylistImage
                images={(rawdata as RecentsForController).tracks.map(track => ({
                  src: track.track_metadata?.album?.pictureUrl ?? '',
                  title: track.track_metadata?.album?.title ?? '',
                }))}
                size={260}
              />
            </div>
          ) : typeof rawdata !== 'undefined' ? (
            <div className="w-full flex justify-center md:w-auto md:flex-none">
              <PlaylistImage
                images={[]}
                size={260}
                placeholderSrc={media}
                singleImage={true}
              />
            </div>
          ) : (
            <div className="w-full flex justify-center md:w-auto md:flex-none">
              <PlaylistImage images={libraryImages ?? []} size={260} />
            </div>
          )}
          <div
            className={cn(
              'flex flex-col-reverse items-end flex-1 min-w-0 w-full',
            )}
          >
            <div className={cn('flex items-center w-full gap-2')}>
              <Typography.Heading
                variant="h2"
                as="h1"
                className={cn('max-w-full', 'break-words', 'overflow-hidden')}
              >
                {playlistTitle}
              </Typography.Heading>
            </div>

            <div className={cn('flex flex-wrap items-center w-full gap-2')}>
              <Typography.Text variant="label" size="md">
                {contentTypeTranslated}
              </Typography.Text>
            </div>
          </div>
        </div>

        <div className={cn('flex md:justify-start gap-4 mt-2 md:mt-6')}>
          {isPersonalPlaylist && typeof rawdata !== 'undefined' ? (
            <div className={cn('flex gap-4')}>
              {(rawdata as PersonalPlaylist).playLists &&
                (rawdata as PersonalPlaylist).playLists.length > 0 && (
                  <PlayAllButton
                    mediaType={ContentTypeVariations.PERSONAL_PLAYLIST}
                    rawData={playAllData}
                    className={cn(
                      'md:w-48 flex items-center gap-2 min-w-[52px]',
                    )}
                    variant="primary"
                  >
                    {isPlaying &&
                    currentCompilationType === 'personal_playlist' &&
                    currentCompilationId ===
                      (rawdata as PersonalPlaylist).playListId.toString()
                      ? t('FOCAL_PAUSE')
                      : t('FOCAL_LISTEN')}
                  </PlayAllButton>
                )}
              <Button
                variant={'ghost'}
                onClick={() => setOptionsMenuOpen(true)}
                aria-label={t('MORE_OPTIONS')}
                aria-haspopup="dialog"
                aria-expanded={optionsMenuOpen}
              >
                <HeroIcon icon={EllipsisHorizontalIcon} size="medium" />
              </Button>
            </div>
          ) : (isFavoritePlaylist || isRecentPlaylist) &&
            typeof rawdata !== 'undefined' ? (
            <PlayAllButton
              mediaType={contentType}
              rawData={rawdata}
              className={cn('md:w-48 flex items-center gap-2 min-w-[52px]')}
              variant="primary"
            >
              {isPlaying &&
              currentCompilationType ===
                (isFavoritePlaylist
                  ? 'personal_favourites'
                  : 'personal_recently_viewed')
                ? t('FOCAL_PAUSE')
                : t('FOCAL_LISTEN')}
            </PlayAllButton>
          ) : (
            <PlayAllButton
              mediaType={contentType}
              rawData={rawdata}
              className={cn('md:w-48 flex items-center gap-2')}
            >
              {isPlaying &&
              rawdata &&
              ('id' in rawdata
                ? rawdata.id.toString()
                : (rawdata as NewsItem).news.newsId) === currentCompilationId
                ? t('FOCAL_PAUSE')
                : t('FOCAL_LISTEN')}
            </PlayAllButton>
          )}
          {isLikable &&
            rawdata &&
            (('id' in rawdata && typeof rawdata.id === 'number') ||
              ('news' in rawdata &&
                typeof rawdata.news.newsId === 'number')) && (
              <LikeButton
                contentId={
                  'id' in rawdata && typeof rawdata.id === 'number'
                    ? rawdata.id
                    : 'news' in rawdata &&
                        typeof rawdata.news.newsId === 'number'
                      ? rawdata.news.newsId
                      : 0 // fallback, should never hit due to outer check
                }
                contentType={
                  (contentType.charAt(0).toUpperCase() +
                    contentType.slice(1)) as keyof typeof FavouriteType
                }
                className="md:w-48 flex items-center gap-2"
                defaultContent={t('FOCAL_LIKE')}
                likedContent={t('FOCAL_LIKED')}
              />
            )}
        </div>
      </header>
      {isPersonalPlaylist && rawdata && (
        <PlaylistOptionsMenu
          drawerOpen={optionsMenuOpen}
          drawerOpenAction={setOptionsMenuOpen}
          playlistTitle={title}
          playlistId={(rawdata as PersonalPlaylist).playListId}
          onRename={setPlaylistTitle}
        />
      )}
    </>
  );
};
